import { useMemo } from 'react';
import { useContent } from './content.jsx';

// "Our team" page helpers, shared by the public page and Admin > Website
// content > Team.

// Flags (flag-icons, MIT): one small SVG file per country, fetched only
// when shown (never inlined into the JavaScript).
const FLAG_FILES = import.meta.glob('../node_modules/flag-icons/flags/4x3/*.svg', { query: '?no-inline', import: 'default', eager: true });
const FLAGS = Object.fromEntries(Object.entries(FLAG_FILES).map(([path, url]) => [path.match(/([a-z-]+)\.svg$/)[1], url]));
export const flagUrl = (code) => (code ? FLAGS[String(code).toLowerCase()] || null : null);

// Country list for the admin dropdown: every 2-letter flag the browser can
// name, sorted in the admin's language.
export function countryOptions(lang) {
  let names;
  try { names = new Intl.DisplayNames([lang === 'en' ? 'en-CA' : 'fr-CA'], { type: 'region' }); } catch { names = null; }
  return Object.keys(FLAGS).filter((c) => c.length === 2 && !['xx', 'eu', 'un'].includes(c))
    .map((c) => ({ code: c.toUpperCase(), name: names?.of(c.toUpperCase()) || c.toUpperCase() }))
    .filter((c) => c.name !== c.code)
    .sort((a, b) => a.name.localeCompare(b.name, lang === 'en' ? 'en' : 'fr'));
}
export function countryName(code, lang) {
  if (!code) return '';
  try { return new Intl.DisplayNames([lang === 'en' ? 'en-CA' : 'fr-CA'], { type: 'region' }).of(code) || code; } catch { return code; }
}

// Emails come from the server reversed + base64 (not plain text in the page
// data); the mailto link is rebuilt only when clicked.
export function decodeEmail(enc) {
  if (!enc) return '';
  try { return [...new TextDecoder().decode(Uint8Array.from(atob(enc), (c) => c.charCodeAt(0)))].reverse().join(''); } catch { return ''; }
}
export function openEmail(enc) {
  const e = decodeEmail(enc);
  if (e) window.location.href = `mailto:${e}`;
}

// "+1 (438) 664-0000" for North American numbers; others as typed.
export function displayPhoneIntl(p) {
  const d = String(p || '').replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return String(p || '');
}
export const telHref = (p) => `tel:+${String(p || '').replace(/\D/g, '')}`;
export const whatsappHref = (p) => `https://wa.me/${String(p || '').replace(/\D/g, '')}`;
export const initials = (name) => String(name || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

export const LANGUAGE_SUGGESTIONS = ['Français', 'English', 'اردو / Urdu', 'हिन्दी / Hindi', 'Punjabi', 'العربية / Arabic', 'Español', 'Deutsch', '中文 / Chinese'];

const bi = (fr, en) => ({ fr, en, auto: null });
const office = (id, country, fr, en, city) => ({ id, hidden: false, name: bi(fr, en), country, city, address: '', phone: '', email: '', members: [] });
// Built-in start: three empty offices, no people. The menu tab stays hidden
// until the page is published.
export const teamDefaults = () => ({
  title: bi('Notre équipe', 'Our team'),
  intro: bi('', ''),
  banner: '',
  bannerMedia: { type: 'photo', image: '', video: '', poster: '', loop: true, speed: 1, text: false, darken: 0 },
  showInMenu: true,
  organization: 'office',
  cardOrder: 'photo-intro',
  levels: defaultLevels(),
  offices: [
    office('mtl', 'CA', 'Canada — Montréal', 'Canada — Montreal', 'Montréal'),
    office('ind', 'IN', 'Inde', 'India', ''),
    office('pak', 'PK', 'Pakistan', 'Pakistan', ''),
  ],
});

// Published team page (null until published) and whether the tab shows.
export function useTeam() {
  const { page, ready } = useContent('team');
  return useMemo(() => ({ page, ready, inMenu: !!page && page.showInMenu !== false }), [page, ready]);
}

// ── Levels (Direction → Team) ────────────────────────────────────────────
// Default levels; each can be renamed, reordered, hidden or removed in the
// admin. `style` is the card preset; the overrides default to 'auto'.
const level = (id, style, fr, en) => ({ id, name: bi(fr, en), showHeading: true, style, layout: 'auto', align: 'center', size: 'auto', photoShape: 'auto', showIntro: 'auto' });
export const defaultLevels = () => [
  level('dir', 'direction', 'Direction', 'Leadership'),
  level('senior', 'senior', 'Haute direction', 'Senior management'),
  level('mgr', 'manager', 'Gestion', 'Management'),
  level('crew', 'crew', 'Notre équipe', 'Our team'),
];
export const levelsOf = (doc) => (doc?.levels?.length ? doc.levels : defaultLevels());

// What each preset looks like: card size, photo shape, intro on the card,
// and how many per row on a phone.
const PRESETS = {
  direction: { size: 'large', photoShape: 'portrait', showIntro: true, phoneCols: 1 },
  senior: { size: 'medium', photoShape: 'rounded', showIntro: true, phoneCols: 2 },
  manager: { size: 'small', photoShape: 'round', showIntro: false, phoneCols: 2 },
  crew: { size: 'small', photoShape: 'round', showIntro: false, phoneCols: 2 },
};

// A level's look for a row of n people: preset + overrides + automatic
// layout (1 → one wide card; 2 → side by side; 3 → 3; 4 → 4, 2×2 on
// tablets; 5+ → centred rows of 3 or 4, last row centred).
export function levelLook(lv, n) {
  const p = PRESETS[lv.style] || PRESETS.crew;
  const size = lv.size && lv.size !== 'auto' ? lv.size : p.size;
  const photoShape = lv.photoShape && lv.photoShape !== 'auto' ? lv.photoShape : size === 'large' ? 'portrait' : p.photoShape;
  const showIntro = lv.showIntro === 'yes' ? true : lv.showIntro === 'no' ? false : p.showIntro;
  let desktop;
  let tablet;
  const fixed = lv.layout && lv.layout !== 'auto' ? Number(lv.layout) : 0;
  if (fixed) { desktop = fixed; tablet = fixed === 4 ? 2 : Math.min(fixed, 3); }
  else if (n <= 3) { desktop = n; tablet = n; }
  else if (n === 4) { desktop = 4; tablet = 2; }
  else { desktop = n === 5 || n === 6 ? 3 : 4; tablet = 3; }
  const phone = size === 'large' ? 1 : Math.min(p.phoneCols, fixed === 1 ? 1 : 2);
  const wide = n === 1 && !fixed && size !== 'small'; // one person: photo left, text right
  // Never more columns than people (one person on a phone = full width).
  return { size, photoShape, showIntro, desktop: Math.min(desktop, Math.max(n, 1)), tablet: Math.min(tablet, desktop, Math.max(n, 1)), phone: Math.min(phone, Math.max(n, 1)), wide, align: lv.align === 'left' ? 'left' : 'center' };
}

// Visible people grouped for display. "Show at the top" people come first
// (by level); then by level for the whole company, or by office (sorted by
// level inside). Members whose level was removed go to the last level.
export function arrangeTeam(doc, countryFilter = 'all') {
  const levels = levelsOf(doc);
  const ids = new Set(levels.map((l) => l.id));
  const last = levels[levels.length - 1]?.id;
  const offices = (doc.offices || []).filter((o) => !o.hidden && (countryFilter === 'all' || o.country === countryFilter));
  const people = [];
  offices.forEach((o, oi) => (o.members || []).forEach((m, mi) => {
    if (m.hidden || !m.name) return;
    people.push({ ...m, level: ids.has(m.level) ? m.level : last, office: o, sort: [m.rank || 0, oi, mi] });
  }));
  const bySort = (a, b) => a.sort[0] - b.sort[0] || a.sort[1] - b.sort[1] || a.sort[2] - b.sort[2];
  const groupByLevel = (list) => levels.map((lv) => ({ level: lv, people: list.filter((p) => p.level === lv.id).sort(bySort) })).filter((g) => g.people.length);
  const top = groupByLevel(people.filter((p) => p.featured));
  const rest = people.filter((p) => !p.featured);
  if (doc.organization === 'level') return { top, sections: [{ office: null, groups: groupByLevel(rest) }].filter((s) => s.groups.length) };
  return {
    top,
    sections: offices.map((o) => ({ office: o, groups: groupByLevel(rest.filter((p) => p.office.id === o.id)) })).filter((s) => s.groups.length),
  };
}
